import { Alert, Box, Card, Dialog, Grid, Stack } from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import useLocales from 'src/hooks/useLocales';
import { useStore } from 'src/stores/store';
import { useMemo } from 'react';
import { IElectricityPaymentDelete } from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';
interface Props {
  id: number;
  year?: number;
  month?: number;
  searchBy?: string;
}

export default observer(function ElectricityPaymentDelete({ id, year, month, searchBy }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { ElectricityPaymentStore } = useStore();
  const {
    deleteElectricityPaymentInfo,
    selectedElectricityPaymentDetail,
    setOpenCloseDeleteDialog,
  } = ElectricityPaymentStore;

  const defaultValues = useMemo<IElectricityPaymentDelete>(
    () => ({
      electricityPaymentId: id,
      electricmeterId: selectedElectricityPaymentDetail?.electricmeterId || undefined,
      remarks: '',
    }),
    [id,selectedElectricityPaymentDetail]
  );
  const validationSchema = Yup.object().shape({
    remarks: Yup.string()
      .required(`${translate('Validation.Remark')}`)
      .label('Remark'),
  });

  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = (data: IElectricityPaymentDelete) => {
    data.electricityPaymentId = id;
    data.electricmeterId = selectedElectricityPaymentDetail?.electricmeterId;
    deleteElectricityPaymentInfo(data)
      .then(() => {
        ElectricityPaymentStore.loadElectricityPayment({
          years: Number(year),
          months: Number(month),
          searchBy: searchBy,
        });
        enqueueSnackbar(`${translate('Tostar.DeleteSuccess')}`);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.message != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Card>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10}>
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack spacing={2}>
                <RHFTextField
                  name="remarks"
                  label={translate('CRUD.Remarks')}
                  size="small"
                  autoFocus
                />
              </Stack>
            </Box>
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack direction="row" spacing={3}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  color="error"
                  loading={isSubmitting}
                  startIcon={<DeleteIcon />}
                >
                  {translate('CRUD.Delete')}
                </LoadingButton>

                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => setOpenCloseDeleteDialog()}
                  startIcon={<CancelIcon />}
                >
                  {translate('CRUD.Cancle')}
                </LoadingButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </FormProvider>
  );
});
